import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from 'chart.js'
import { format, subMonths } from 'date-fns'
import React, { useState } from 'react'
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { Bar, Doughnut } from 'react-chartjs-2'
import { useSelector } from 'react-redux'
import Activity from '../components/Activity'
import { useGetCorporateDashQuery, useGetOverviewAnalysisQuery } from '../redux/api/overviewApi'
import { useGetSpecialityQuery } from '../redux/api/specialityApi'
import { useGetReportsQuery } from '../redux/api/brainApi'
import NoReports from '../components/NoReports'
import ReactApexChart from 'react-apexcharts'

ChartJS.register(ArcElement, Tooltip, PointElement, LineElement, Legend, CategoryScale, LinearScale, BarElement, Title)

const OverviewCorp = () => {
  const { selectedUser, isGroup } = useSelector((state) => state.user)

  const [filter, setFilter] = useState({
    gender: 'all',
    startDate: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  })

  const { data: reports, isLoading: isReports } = useGetReportsQuery({ id: selectedUser?.id, test_area: 'all' }, { skip: !selectedUser })

  const { data: specialities = [], isLoading: loadingSpecialities } = useGetSpecialityQuery({}, { skip: isReports || !reports?.length })
  const { data: corporateDashData, isLoading: loadingCorporateDash } = useGetCorporateDashQuery(
    { id: selectedUser?.id, isGroup },
    { skip: (isGroup && !selectedUser) || isReports || !reports?.length }
  )
  const { data: overviewData, isLoading: loadingOverview } = useGetOverviewAnalysisQuery(
    {
      id: selectedUser?.id,
      isGroup,
      gender: filter.gender,
      startDate: filter.startDate,
      endDate: filter.endDate,
      specialities,
    },
    { skip: (isGroup && !selectedUser) || loadingSpecialities || isReports || !reports?.length }
  )

  const doughnutLabel = {
    id: 'doughnutLabel',
    beforeDatasetsDraw(chart, args, pluginOptions) {
      const { ctx, data } = chart
      ctx.save()

      const xCoor = chart.getDatasetMeta(0).data[0].x
      const yCoor = chart.getDatasetMeta(0).data[0].y
      ctx.font = 'bold 24px sans-serif'
      ctx.fillStyle = 'black'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'end'
      ctx.fillText(`${Math.abs(data.datasets[0].data[0])}%`, xCoor, yCoor)
    },
  }

  return (
    <>
      <div className='dashboard-heading'>
        <div className='content-container'>
          <h2>Hi there</h2>
          <p>Here’s a quick overview for your reports</p>
        </div>
        <Activity />
      </div>

      {isReports ? (
        <p>Loading...</p>
      ) : !reports || reports.length === 0 ? (
        <div className='d-flex justify-content-center'>
          <NoReports />
        </div>
      ) : loadingSpecialities || loadingCorporateDash || loadingOverview ? (
        'Loading...'
      ) : !overviewData || !corporateDashData ? (
        'No Data'
      ) : (
        <div className='dashboard-cards'>
          {/* ------- Filters -------- */}
          <div className='d-flex flex-wrap align-items-center gap-4'>
            {/* <div className='rounded-pill d-flex gap-2 p-1' style={{ backgroundColor: '#D9D9D9' }}>
              {['all', 'male', 'female', 'prefer_not_to_say'].map((gender) => (
                <button
                  key={gender}
                  type='button'
                  onClick={() => setFilter((old) => ({ ...old, gender: gender }))}
                  className={`${
                    filter.gender === gender ? 'dashboard-cards' : 'bg-transparent'
                  } rounded-pill px-3 py-1 m-0 outline-none border-0 text-black text-capitalize`}
                >
                  {gender.replaceAll('_', ' ')}
                </button>
              ))}
            </div> */}
            <div className='d-flex align-items-center gap-3'>
              <FloatingLabel controlId='gender' label='Gender'>
                <Form.Select
                  required
                  type='select'
                  value={filter.gender}
                  onChange={(e) => setFilter({ ...filter, gender: e.target.value })}
                  className='text-capitalize'
                >
                  {['all', 'male', 'female', 'prefer_not_to_say'].map((gender, i) => (
                    <option key={i} value={gender}>
                      {gender.replaceAll('_', ' ')}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>Please enter required field.</Form.Control.Feedback>
              </FloatingLabel>
              <FloatingLabel controlId='start' label='Start Date'>
                <Form.Control required type='date' value={filter.startDate} onChange={(e) => setFilter({ ...filter, startDate: e.target.value })} />
                <Form.Control.Feedback type='invalid'>Please enter required field.</Form.Control.Feedback>
              </FloatingLabel>
              <FloatingLabel controlId='end' label='End Date'>
                <Form.Control required type='date' value={filter.endDate} onChange={(e) => setFilter({ ...filter, endDate: e.target.value })} />
                <Form.Control.Feedback type='invalid'>Please enter required field.</Form.Control.Feedback>
              </FloatingLabel>
            </div>
          </div>

          {/* ------- Row 1 -------- */}
          <Row className=''>
            <Col xs={12} lg={8}>
              <div className='rounded-pill px-3 py-2' style={{ backgroundColor: 'var(--primary-color)', margin: '12px auto', width: 'fit-content' }}>
                <h4 className='m-0 text-white text-center'>Hiring/Rehiring</h4>
              </div>

              <Row className='justify-content-center'>
                {Object.entries(corporateDashData)
                  .reverse()
                  .map(([k, v], i) => {
                    let info = {
                      attrition: `Have a ${v > 0 ? 'high' : 'low'} tendency to switch`,
                      burnout: `Are on ${v > 0 ? 'higher' : 'lower'} than average burnout`,
                      productivity: `Reported ${v > 0 ? 'healthy' : 'lower than average'} productivity level`,
                    }
                    let data = getDonutData(k, v)

                    return (
                      <Col key={i} sm={4} xxl={3}>
                        <h4 className='text-capitalize text-center opacity-100 text-black m-0 my-2' style={{ letterSpacing: 'revert' }}>
                          {k}
                        </h4>
                        <div>
                          <Doughnut
                            data={data}
                            height='125px'
                            width='125px'
                            plugins={[doughnutLabel]}
                            options={{
                              maintainAspectRatio: false,
                              plugins: {
                                tooltip: {
                                  enabled: false,
                                },
                              },
                            }}
                          />
                        </div>
                        <p className='my-2 text-center'>{info[k]}</p>
                      </Col>
                    )
                  })}
              </Row>
            </Col>

            <Col xs={12} lg={4}>
              <div className='rounded-pill px-3 py-2' style={{ backgroundColor: 'var(--primary-color)', margin: '12px auto', width: 'fit-content' }}>
                <h4 className='m-0 text-white text-center'>Improvements</h4>
              </div>

              <Row className='justify-content-center'>
                {Object.entries(overviewData.ms).map(([k, v], i) => {
                  let color
                  if (v > 0) {
                    color = k === 'Fatigue' ? '#be5f5a' : '#15a48f'
                  } else {
                    color = k === 'Fatigue' ? '#15a48f' : '#be5f5a'
                  }
                  return (
                    <Col key={i} sm={6} md={4} lg={6}>
                      <h4 className='text-capitalize text-center opacity-100 text-black m-0 my-2' style={{ letterSpacing: 'revert' }}>
                        {k}
                      </h4>
                      <div>
                        <Doughnut
                          data={{
                            datasets: [
                              {
                                data: [v, 100 - v],
                                backgroundColor: [color, '#D9D9D9'],
                                borderRadius: 4,
                                borderWidth: 0,
                                cutout: '60%',
                              },
                            ],
                          }}
                          height='125px'
                          width='125px'
                          plugins={[doughnutLabel]}
                          options={{
                            maintainAspectRatio: false,
                            plugins: {
                              tooltip: {
                                enabled: false,
                              },
                            },
                          }}
                        />
                      </div>
                      <p className='my-2 text-center'>
                        {k} {v > 0 ? 'increased' : 'decreased'} by {Math.abs(v)}%
                      </p>
                    </Col>
                  )
                })}
              </Row>
            </Col>
          </Row>
          {/* ------- Row 2 -------- */}
          <Row className='justify-content-between align-items-center'>
            <Col>
              <div className='rounded-pill px-3 py-2' style={{ backgroundColor: 'var(--primary-color)', margin: '12px auto', width: 'fit-content' }}>
                <h4 className='m-0 text-white text-center'>Team Strengths</h4>
              </div>

              {/* <Bar data={getBarData(overviewData.brainStrength)} options={options} height={170} /> */}

              <ReactApexChart
                series={getBarData(overviewData.brainStrength).series}
                options={getBarData(overviewData.brainStrength).options}
                type='bar'
                height={350}
              />
            </Col>
          </Row>
        </div>
      )}
    </>
  )
}

export default OverviewCorp

const getColor = (val) => {
  return val <= 30 ? '#be5f5a' : val <= 60 ? '#BDA25C' : '#15a48f'
}

const getDonutData = (key, value) => {
  let color = key === 'burnout' ? '#be5f5a' : key === 'attrition' ? getColor(value) : '#15a48f'

  return {
    datasets: [
      {
        data: [Math.round(value), 100 - value],
        backgroundColor: [color, '#D9D9D9'],
        borderRadius: 4,
        borderWidth: 0,
        cutout: '60%',
      },
    ],
  }
}

const getBarData = (data) => {
  let labels = Object.keys(data).map((l) => l.at(0).toUpperCase() + l.slice(1))
  let d = Object.values(data).map(({ association, interest, focus, stress }) => {
    return [association, interest, focus, stress]
  })

  const series = ['Association', 'Interactive', 'Focus', 'Stress'].map((label, i) => {
    return {
      name: label,
      data: d.map((v) => v[i]),
    }
  })

  const options = {
    chart: {
      height: 350,
      type: 'bar',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 12,
        borderRadiusApplication: 'end',
      },
    },
    yaxis: {
      tickAmount: 4,
      min: 0,
      max: 100,
      labels: {
        formatter: function (value) {
          return value + '%'
        },
      },
    },
    colors: ['#2b8bba', '#40b9d6', '#7dc9bd', '#19a393'],
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: labels,
    },

    fill: {
      opacity: 1,
    },
  }

  return { series, options }

  // return {
  //   labels: labels,
  //   datasets: [
  //     {
  //       label: 'Association',
  //       data: d.map((val) => val[0]),
  //       backgroundColor: '#2b8bba',
  //       borderRadius: 8,
  //       minBarLength: 2,
  //       maxBarThickness: 50,
  //     },
  //     {
  //       label: 'Interactive',
  //       data: d.map((val) => val[1]),
  //       backgroundColor: '#40b9d6',
  //       borderRadius: 8,
  //       minBarLength: 2,
  //       maxBarThickness: 50,
  //     },
  //     {
  //       label: 'Focus',
  //       data: d.map((val) => val[2]),
  //       backgroundColor: '#7dc9bd',
  //       borderRadius: 8,
  //       minBarLength: 2,
  //       maxBarThickness: 50,
  //     },
  //     {
  //       label: 'Stress',
  //       data: d.map((val) => val[3]),
  //       backgroundColor: '#19a393',
  //       borderRadius: 8,
  //       minBarLength: 2,
  //       maxBarThickness: 50,
  //     },
  //   ],
  // }
}

const options = {
  plugins: {
    tooltip: {
      displayColors: false,
    },

    legend: {
      position: 'top',
      align: 'end',
    },
  },

  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      min: 0,
      max: 100,
      ticks: {
        stepSize: 25,
        callback: (value) => {
          return value + '%'
        },
      },
    },
  },
}
